import React, { useState, useEffect } from 'react';

const ScreenshotPreview = ({
  title = "Screenshot Preview",
  isPolling,
  screenshots = [], 
  testStatus,
  pollingMessage = "Running...",
  loadingMessage = "Running workflow steps...",
  successMessage = "All steps completed successfully.",
  onReset,
  onRun,
  runButtonDisabled = false,
  runButtonText = "Run from Start",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [imgError, setImgError] = useState(false);
  
  useEffect(() => {
    if (screenshots.length > 0) {
      setActiveIndex(screenshots.length - 1);
      setImgError(false);
    }
  }, [screenshots.length]);
  
  const activeScreenshot = screenshots[activeIndex];
  
  const handlePrevious = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
      setImgError(false);
    }
  };
  
  const handleNext = () => {
    if (activeIndex < screenshots.length - 1) {
      setActiveIndex(activeIndex + 1);
      setImgError(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          {isPolling && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-b-transparent"></div>
              <p className="text-blue-700 font-medium text-sm">
                {pollingMessage}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-200 shadow-md">
            {activeScreenshot && !imgError ? (
              <img 
                src={activeScreenshot} 
                alt="Browser screenshot"
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.error('Image failed to load:', e);
                  setImgError(true);
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800/50 backdrop-blur-[2px]">
                <div className="bg-gray-900/70 px-6 py-3 rounded-lg backdrop-blur-sm">
                  <p className="text-white font-medium">
                    {isPolling ? "Running..." : imgError ? "Failed to load image" : "Waiting for screenshot..."}
                  </p>
                </div>
              </div>
            )}

            {isPolling && activeScreenshot && !imgError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800/30 backdrop-blur-[1px]">
                <div className="bg-gray-900/70 px-6 py-3 rounded-lg backdrop-blur-sm">
                  <p className="text-white font-medium">{loadingMessage}</p>
                </div>
              </div>
            )}
            
            {screenshots.length > 1 && (
              <>
                {/* Left arrow */}
                <button 
                  onClick={handlePrevious}
                  disabled={activeIndex === 0}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all ${
                    activeIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Right arrow */}
                <button 
                  onClick={handleNext}
                  disabled={activeIndex === screenshots.length - 1}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all ${
                    activeIndex === screenshots.length - 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Screenshot counter */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  {activeIndex + 1} / {screenshots.length}
                </div>
              </>
            )}
          </div>

          {testStatus === 'success' && (
            <div className="mt-2 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Success!</span>
                <span className="ml-2">{successMessage}</span>
              </div>
            </div>
          )}
          
          {screenshots.length > 1 && (
            <div className="mt-2 flex justify-center overflow-x-auto py-2">
              <div className="flex space-x-2">
                {screenshots.map((screenshot, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setActiveIndex(index);
                      setImgError(false);
                    }}
                    className={`w-16 h-10 bg-gray-200 rounded overflow-hidden flex-shrink-0 transition-all ${
                      activeIndex === index ? 'ring-2 ring-blue-500 ring-offset-1' : 'opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={screenshot} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Thumbnail failed to load:', e);
                        // Do not set the error state for thumbnails
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Control buttons */}
      <div className="flex gap-2 mt-4">
      <button
        onClick={onRun}
        disabled={runButtonDisabled}
        className={`flex-1 ${
          isPolling ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
        } text-white font-medium py-2 px-4 rounded transition-colors flex items-center justify-center gap-2 ${
          runButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {isPolling ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        {runButtonText}
      </button>

        <button
          onClick={onReset}
          disabled={isPolling}
          className={`flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded transition-colors flex items-center justify-center gap-2 ${
            isPolling ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset Browser
        </button>
      </div>
    </div>
  );
};

export default ScreenshotPreview;